import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

interface LazyImageProps {
  src: string;
  alt: string;
  placeholder?: string;
  threshold?: number;
  rootMargin?: string;
  onLoad?: () => void;
  onError?: () => void;
  wrapperClassName?: string;
  className?: string;
  width?: number | string;
  height?: number | string;
  style?: React.CSSProperties;
}

const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  placeholder = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PC9zdmc+',
  threshold = 0.1,
  rootMargin = '50px',
  onLoad,
  onError,
  wrapperClassName = '',
  className = '',
  width,
  height,
  style
}) => {
  const [imageSrc, setImageSrc] = useState<string>(placeholder);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 预加载图片
            const img = new Image();
            img.src = src;
            
            img.onload = () => {
              setImageSrc(src);
              setIsLoaded(true);
              onLoad?.();
            };
            
            img.onerror = () => {
              setIsError(true);
              onError?.();
            };
            
            // 停止观察
            const currentRef = imgRef.current;
            if (currentRef) {
              observer.unobserve(currentRef);
            }
          }
        });
      },
      {
        threshold,
        rootMargin
      }
    );

    const currentRef = imgRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [src, threshold, rootMargin, onLoad, onError]);

  return (
    <div ref={imgRef} className={`relative overflow-hidden ${wrapperClassName}`}>
      <motion.img
        initial={{ opacity: 0 }}
        animate={{ opacity: isLoaded ? 1 : 0.5 }}
        transition={{ duration: 0.3 }}
        src={imageSrc}
        alt={alt}
        className={`${className} ${isError ? 'filter grayscale' : ''}`}
        loading="lazy"
        decoding="async"
        width={width}
        height={height}
        style={style}
      />
      
      {/* 加载状态 */}
      {!isLoaded && !isError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
          <div className="animate-pulse">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      )}
      
      {/* 错误状态 */}
      {isError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
          <div className="text-center text-gray-500 dark:text-gray-400">
            <i className="fas fa-image text-2xl mb-2"></i>
            <p className="text-xs">图片加载失败</p>
          </div>
        </div>
      )}
    </div>
  );
};

// 优化的背景图片懒加载组件
export const LazyBackgroundImage: React.FC<{
  src: string;
  className?: string;
  children?: React.ReactNode;
  threshold?: number;
}> = ({ src, className = '', children, threshold = 0.1 }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = new Image();
            img.src = src;
            img.onload = () => setIsLoaded(true);
            
            const currentRef = ref.current;
            if (currentRef) {
              observer.unobserve(currentRef);
            }
          }
        });
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [src, threshold]);

  return (
    <div
      ref={ref}
      className={`${className} transition-opacity duration-500`}
      style={{
        backgroundImage: isLoaded ? `url(${src})` : undefined,
        opacity: isLoaded ? 1 : 0.5
      }}
    >
      {children}
    </div>
  );
};

export default LazyImage; 